/**
 * OKS-768 — Single source of truth for the masterclass-cover aspect ratio.
 *
 * OKS-834 — retargeted 16:5 → 3:2 to match the mission-cover ratio (one
 * shared library cover shape). Proof the "one edit point" held: this was a
 * two-constant + model-variant flip, no display-surface changes. Legacy
 * pre-OKS-834 covers regenerate the variant lazily to 3:2 (mild
 * center-crop) on next request — no backfill.
 *
 * Until OKS-768 masterclass covers had NO locked ratio: the banner was
 * `h-28 w-full object-cover` and the server `:card` variant was
 * `resize_to_fill: [800, 224]` (25:7), so the displayed ratio actually
 * varied by container width AND the server center-cropped the admin's
 * frame. OKS-768 crops-on-upload to a fixed ratio and displays at that
 * same ratio on every banner surface so the framed image appears
 * pixel-identical everywhere (client-crop-authoritative, mirroring the
 * shipped OKS-697 mission-cover pattern). The ratio lives HERE, once, so a
 * future ratio change is a single edit — retarget these two constants +
 * the model's `Masterclass::COVER_CARD_VARIANT` and every surface follows.
 *
 * Two shapes are exported because two consumers need different forms:
 *
 *   - `MASTERCLASS_COVER_ASPECT_CLASS` — the Tailwind utility class string
 *     consumed by every banner display container (`cn(…CLASS, …)`). It
 *     MUST remain a literal `"aspect-[3/2]"` string in THIS file:
 *     Tailwind v4's JIT scans source for arbitrary-value utilities, so the
 *     literal has to exist somewhere it can see it. Keeping it here (and
 *     only here) is what makes the "one edit point" guarantee real —
 *     surfaces import the symbol rather than re-typing the class, so none
 *     can silently drift back to `h-28`. LQE's Vitest parity test imports
 *     this exact symbol.
 *
 *   - `MASTERCLASS_COVER_ASPECT_RATIO` — the numeric ratio (width /
 *     height) consumed by `react-easy-crop`'s `aspect` prop and the crop
 *     area math. Kept in lockstep with the class + the server variant
 *     geometry (`Masterclass::COVER_CARD_VARIANT = [1200, 800]` →
 *     1200 / 800 = 1.5 = 3 / 2).
 *
 * NOTE — banner ratio. This ratio governs every `:card` banner surface:
 * form preview, brand card, creator Learn detail hero, AND — since
 * OKS-836 — the creator Learn *card*, which was redesigned from a square
 * `:thumb` tile to a full 3:2 banner so the list and detail show the same
 * frame (framed==shown). The square `:thumb` variant (`[112, 112]`) is no
 * longer used by the Learn list; it survives only for the compact
 * completed-masterclasses item (`crew/creator-completed-masterclasses-item`,
 * swatch-only) and is intentionally OUTSIDE "framed==shown" (a
 * center-square derivation cannot be both 3:2 and square).
 *
 * @see app/models/masterclass.rb `COVER_CARD_VARIANT`
 */

/**
 * Tailwind aspect-ratio utility for every masterclass banner container.
 * Literal string (see module note) — do not build it dynamically.
 */
export const MASTERCLASS_COVER_ASPECT_CLASS = "aspect-[3/2]"

/**
 * Numeric masterclass banner ratio (width / height = 3 / 2 = 1.5). Feeds
 * `react-easy-crop` `aspect` and the canvas crop-area math. Mirrors the
 * server `:card` variant geometry (`[1200, 800]`).
 */
export const MASTERCLASS_COVER_ASPECT_RATIO = 3 / 2
